import classNames from 'classnames';
import React, { ChangeEvent, FC } from 'react';

import { FieldWrapper, FieldWrapperProps } from '../FieldWrapper';

import PasswordVisibilityToggle from 'pages/auth/components/PasswordVisibilityToggle';
import { getAuthAutoComplete, usePasswordVisibility, useSyncAutofillValue } from 'hooks';

type TextFieldProps = FieldWrapperProps & {
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  name?: string;
  value?: string;
  placeholder?: string;
  type?: string | undefined;
  maxLength?: number;
  autoComplete?: string;
  labelClassName?: string;
  inputClassName?: string;
};

const TextField: FC<TextFieldProps> = ({
  value,
  placeholder,
  name,
  type,
  maxLength,
  autoComplete,
  onChange,
  onFocus,
  onBlur,
  isDisabled,
  inputClassName,
  className,
  ...fieldWrapperProps
}) => {
  const { isVisible, inputRef, toggleVisibility, visibilityKey } = usePasswordVisibility();
  const isPasswordType = type === 'password';
  const inputType = isPasswordType && isVisible ? 'text' : type;

  const commitValue = (nextValue: string) => {
    onChange?.({
      target: { value: nextValue, name: name ?? '' },
    } as ChangeEvent<HTMLInputElement>);
  };

  useSyncAutofillValue(inputRef, value, commitValue, Boolean(isPasswordType || type === 'email'));

  const handleBlur = () => {
    const nativeValue = inputRef.current?.value;
    if (nativeValue != null && nativeValue !== value) {
      commitValue(nativeValue);
    }
    onBlur?.();
  };

  return (
    <FieldWrapper
      isDisabled={isDisabled}
      className={className}
      {...fieldWrapperProps}
      rightChildren={
        isPasswordType && name ? (
          <PasswordVisibilityToggle
            isVisible={isVisible}
            onToggle={() => toggleVisibility(commitValue)}
            inputId={name}
          />
        ) : (
          fieldWrapperProps.rightChildren
        )
      }
    >
      <input
        key={isPasswordType ? `${name}-${visibilityKey}` : undefined}
        ref={inputRef}
        id={name}
        name={name}
        value={isDisabled && !value ? 'Unissued' : value}
        placeholder={placeholder}
        autoComplete={autoComplete ?? getAuthAutoComplete(type, name)}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={handleBlur}
        disabled={isDisabled}
        type={inputType}
        maxLength={maxLength}
        className={classNames(
          'w-full focus:outline-none bg-inherit',
          isDisabled && !value && 'text-[#C4CBDD]',
          inputClassName,
        )}
      />
    </FieldWrapper>
  );
};

export default TextField;
